/**
 * EQuIP Patient Query Hooks
 *
 * React Query hooks for patient measure detail endpoints.
 * Wraps equipApi.dashboard.getPatientMeasures with a typed key factory
 * and enabled guard for conditional parameters.
 */

import { useQuery } from '@tanstack/react-query'
import { equipApi } from '@/services/equipApi'
import type { PatientMeasureDetail } from '@/lib/equip-types'

// ---------------------------------------------------------------------------
// Key factory
// ---------------------------------------------------------------------------

export const equipPatientKeys = {
  all: () => ['equip', 'patients'] as const,
  patientMeasures: (patientId: string, year: number, quarter: number) =>
    ['equip', 'patients', 'measures', patientId, year, quarter] as const,
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches all measure details for a specific patient.
 * Query is disabled when patientId is null/undefined/empty — no request fires
 * when the patient modal is closed.
 */
export function usePatientMeasures(
  patientId: string | undefined,
  year: number,
  quarter: number
) {
  return useQuery<PatientMeasureDetail>({
    queryKey: equipPatientKeys.patientMeasures(patientId ?? '', year, quarter),
    queryFn: () =>
      equipApi.dashboard.getPatientMeasures(patientId!, year, quarter),
    enabled: !!patientId,
  })
}
